import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Star, Zap, Heart } from 'lucide-react';
import { toast } from 'react-hot-toast'; // Para mostrar notificaciones
import { useAddToWishlist } from '../../functions/wishlistHook'; // Importar el hook de wishlist
import { useAddDigitalAssetToCart } from '../../functions/digitalAssetHook'; // Importar el hook de carrito
import Unsplash from './unsplash.jpg';

interface DigitalAsset {
  _id: string;
  title: string;
  description: string;
  price: number;
  discountPrice?: number;
  previewUrl: string;
  category: string;
  featured?: boolean;
  rating?: number;
  downloads?: number;
  tags: string[];
}

interface AssetCardProps {
  asset: DigitalAsset;
}

export const AssetCard: React.FC<AssetCardProps> = ({ asset }) => {
  // Hook para agregar a la wishlist
  const { mutate: addToWishlist, isLoading: isAddingToWishlist } = useAddToWishlist();

  // Hook para agregar al carrito
  const { mutate: addToCart, isLoading: isAddingToCart } = useAddDigitalAssetToCart();

  // Manejar la acción de agregar al carrito
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  
    // Llamar al hook para agregar al carrito
    addToCart(
      { digitalAssetId: asset._id },
      {
        onSuccess: (data) => {
          toast.success(data?.message || 'Activo digital agregado al carrito con éxito.');
        },
        onError: (error) => {
          // El mensaje de error ya se maneja en el hook, no es necesario repetirlo aquí
        },
      }
    );
  };

  // Manejar la acción de agregar a la wishlist
  const handleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Llamar al hook para agregar a la wishlist
    addToWishlist(
      { digitalAssetId: asset._id },
      {
        onSuccess: (data) => {
          toast.success(data?.message || 'Activo digital agregado a la wishlist con éxito.');
        },
        onError: (error) => {
          toast.error(error?.message || 'Error al agregar a la wishlist.');
        },
      }
    );
  };

  return (
    <Link
      to={`/asset/${asset._id}`}
      className="group relative bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden min-w-[320px] max-w-[400px] mx-auto hover:-translate-y-1.5"
    >
      {/* Image Container */}
      <div className="relative aspect-[16/9] overflow-hidden">
        <img
          src={Unsplash}
          alt={asset.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />

        {/* Top Badges */}
        <div className="absolute top-3 left-3 right-3 flex justify-between items-start">
          {asset.featured && (
            <div className="flex items-center bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 text-sm font-semibold rounded-full">
              <Star className="w-4 h-4 mr-1" />
              <span>Destacado</span>
            </div>
          )}
        </div>

        {/* Favorite Button - Fuera del overlay */}
        <button
          type="button"
          title="Add to favorites"
          onClick={handleFavorite}
          disabled={isAddingToWishlist} // Deshabilitar el botón mientras se carga
          className="absolute top-3 right-3 z-20 p-2 bg-white/90 backdrop-blur rounded-full hover:bg-red-50 transition-colors shadow-sm"
        >
          <Heart className="w-5 h-5 text-gray-600 hover:text-red-500 fill-transparent hover:fill-current" />
        </button>

        {/* Overlay on Hover */}
        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
          <button
            type="button"
            title="Add to cart"
            onClick={handleAddToCart}
            disabled={isAddingToCart} // Deshabilitar el botón mientras se carga
            className="flex items-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-full font-semibold hover:bg-gray-50 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300 shadow-lg"
          >
            <ShoppingCart className="w-5 h-5" />
            <span>{isAddingToCart ? 'Agregando...' : 'Agregar al carrito'}</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 space-y-4">
        <div className="flex justify-between items-start">
          <div className="pr-4">
            <h3 className="text-xl font-bold text-gray-900 mb-1">{asset.title}</h3>
            <p className="text-sm text-gray-500 font-medium">{asset.category}</p>
          </div>
          <div className="text-right min-w-[80px]">
            {asset.discountPrice ? (
              <>
                <span className="text-red-600 font-bold text-lg">${asset.discountPrice}</span>
                <span className="block text-sm text-gray-400 line-through mt-[-2px]">${asset.price}</span>
              </>
            ) : (
              <span className="font-bold text-lg text-gray-900">${asset.price}</span>
            )}
          </div>
        </div>

        {/* Metadata */}
        <div className="flex items-center gap-4 text-sm text-gray-600 border-t pt-4">
          {asset.rating && (
            <div className="flex items-center gap-1.5">
              <Star className="w-5 h-5 text-yellow-400 fill-current" />
              <span className="font-medium">{asset.rating}</span>
            </div>
          )}
          {asset.downloads && (
            <div className="flex items-center gap-1.5">
              <Zap className="w-5 h-5 text-blue-500 fill-current" />
              <span className="font-medium">{asset.downloads.toLocaleString()} descargas</span>
            </div>
          )}
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          {asset.tags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
};